import type { ReactNode } from "react";
import {
  Check,
  ChevronDown,
  Home as HomeIcon,
  LogOut,
  Plus,
  Settings,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/dropdown-menu";
import { useCurrentWorkspace } from "@/data/workspaces";
import { env } from "@/env";

export interface SidebarItem {
  id: string;
  title: ReactNode;
  when: string;
  pinned?: boolean;
  highlighted?: boolean;
}

interface SidebarProps {
  homeActive: boolean;
  pinned: SidebarItem[];
  recent: SidebarItem[];
  onOpenHome: () => void;
  onOpenSession: (id: string) => void;
  onOpenSettings: () => void;
  onSwitchWorkspace?: (slug: string) => void;
  onOpenOnboarding?: () => void;
  onLogout: () => void | Promise<void>;
}

export function Sidebar({
  homeActive,
  pinned,
  recent,
  onOpenHome,
  onOpenSession,
  onOpenSettings,
  onSwitchWorkspace,
  onOpenOnboarding,
  onLogout,
}: SidebarProps) {
  const { currentWorkspace, memberships, currentSlug } = useCurrentWorkspace();
  const displayName = currentWorkspace?.name ?? "Parable";
  const displayInitial = displayName.slice(0, 1).toUpperCase();
  return (
    <aside className="flex w-[248px] shrink-0 flex-col gap-1 border-r border-border bg-sidebar p-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Workspace: ${displayName} — open menu`}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors outline-none hover:bg-background focus-visible:ring-2 focus-visible:ring-ring"
          >
            {currentWorkspace?.logo_url ? (
              <img
                src={`${env.API_BASE_URL}${currentWorkspace.logo_url}`}
                alt=""
                className="h-[26px] w-[26px] rounded-md object-cover"
              />
            ) : (
              <div className="grid h-[26px] w-[26px] place-items-center rounded-md bg-foreground text-[14px] font-bold tracking-tight text-background">
                {displayInitial}
              </div>
            )}
            <div className="text-[14px] font-semibold tracking-tight">
              {displayName}
            </div>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={4} className="w-[260px]">
          {memberships.map((m) => {
            const isCurrent = m.workspace.slug === currentSlug;
            return (
              <DropdownMenuItem
                key={m.workspace.id}
                onSelect={() => {
                  if (!isCurrent && onSwitchWorkspace) {
                    onSwitchWorkspace(m.workspace.slug);
                  }
                }}
              >
                {isCurrent ? (
                  <Check className="h-3.5 w-3.5 opacity-70" />
                ) : (
                  <span className="h-3.5 w-3.5" />
                )}
                <span className="truncate">{m.workspace.name}</span>
              </DropdownMenuItem>
            );
          })}
          {memberships.length > 0 && <DropdownMenuSeparator />}
          {onOpenOnboarding && (
            <DropdownMenuItem onSelect={onOpenOnboarding}>
              <Plus className="h-3.5 w-3.5 opacity-70" />
              Create workspace
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onOpenSettings}>
            <Settings className="h-3.5 w-3.5 opacity-70" />
            Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => {
              void onLogout();
            }}
          >
            <LogOut className="h-3.5 w-3.5 opacity-70" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <SidebarItem
        icon={<HomeIcon className="h-4 w-4 opacity-70" />}
        active={homeActive}
        onClick={onOpenHome}
      >
        Home
      </SidebarItem>

      {pinned.length > 0 ? (
        <>
          <SidebarLabel>Pinned</SidebarLabel>
          {pinned.map((row) => (
            <SessionRow
              key={row.id}
              title={row.title}
              when={row.when}
              highlighted={row.highlighted}
              onClick={() => onOpenSession(row.id)}
            />
          ))}
        </>
      ) : null}

      <SidebarLabel>Recent</SidebarLabel>
      {recent.length === 0 ? (
        <div className="px-2.5 py-1 text-[12px] text-muted-foreground">
          No captures yet
        </div>
      ) : (
        recent.map((row) => (
          <SessionRow
            key={row.id}
            title={row.title}
            when={row.when}
            highlighted={row.highlighted}
            onClick={() => onOpenSession(row.id)}
          />
        ))
      )}

      <div className="flex-1" />
    </aside>
  );
}

function SidebarLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-2.5 pt-3.5 pb-1.5 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
      {children}
    </div>
  );
}

function SidebarItem({
  children,
  icon,
  active,
  onClick,
}: {
  children: ReactNode;
  icon?: ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-[13px] transition-colors ${
        active
          ? "bg-card font-medium text-foreground"
          : "text-muted-foreground hover:bg-background"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function SessionRow({
  title,
  when,
  highlighted = false,
  onClick,
}: {
  title: ReactNode;
  when: string;
  highlighted?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`grid w-full cursor-pointer grid-cols-[1fr_auto] items-center gap-2 rounded-md px-2.5 py-1 text-left transition-colors ${
        highlighted ? "bg-card text-foreground" : "hover:bg-background"
      }`}
    >
      <div className="overflow-hidden text-[13px] text-ellipsis whitespace-nowrap text-muted-foreground">
        {title}
      </div>
      <div className="text-[11px] text-muted-foreground">{when}</div>
    </button>
  );
}
